import React, { useEffect, useId, useRef } from 'react';
import { AlertTriangle, X } from 'lucide-react';

interface WizardChoiceChangedModalProps {
  isOpen: boolean;
  title: string;
  description: string;
  keepLabel: string;
  redoLabel: string;
  onKeep: () => void;
  onRedo: () => void;
  onClose: () => void;
}

// Vraag bij een gewijzigde keuze wat er met de vervolgstap moet gebeuren
// (bijvoorbeeld: andere titel, intro's opnieuw schrijven of houden).
// Bewust een expliciete vraag, geen verborgen vlag die het beslist.
export function WizardChoiceChangedModal({
  isOpen,
  title,
  description,
  keepLabel,
  redoLabel,
  onKeep,
  onRedo,
  onClose,
}: WizardChoiceChangedModalProps) {
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  // Focus in de dialoog zetten, Escape sluit, en daarna de focus teruggeven.
  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="bg-white rounded-lg shadow-xl max-w-md w-full focus:outline-none"
      >
        <div className="p-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-yellow-600" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <h2 id={titleId} className="text-xl font-bold text-[#231F20] mb-2">
                {title}
              </h2>
              <p id={descriptionId} className="text-sm text-gray-600">
                {description}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Sluiten"
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
            <button type="button" onClick={onKeep} className="btn-secondary">
              {keepLabel}
            </button>
            <button type="button" onClick={onRedo} className="btn-primary">
              {redoLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
